import {DependencyList, ReactNode, useContext, useLayoutEffect} from 'react';
import {WidgetActionsContext} from '../../components/WidgetCard';

/**
 * Declares what the widget card header should render in its actions slot.
 *
 * Call this inside any dashboard widget component. The actions appear in the
 * card header alongside the title — the same place that {@code renderActions}
 * in the plugin config used to occupy. This hook replaces that config-level
 * function so that the widget component owns its own header actions, keeping
 * the concern in one place.
 *
 * Uses {@code useLayoutEffect} so the header is populated synchronously with
 * the first paint — no empty-flash on mount.
 *
 * @param getActions Factory that returns the ReactNode to render. List every
 *                   value captured inside it in {@code deps}, exactly like
 *                   {@code useMemo} or {@code useCallback}.
 * @param deps       Re-runs when any dep changes so actions stay in sync with
 *                   widget state (e.g. a loading spinner on a button).
 *
 * @example — static action
 * useWidgetActions(
 *   () => <Button onClick={handleOpenModal}>Beantragen</Button>,
 *   [handleOpenModal],
 * );
 *
 * @example — reactive action (loading state reflected in header button)
 * useWidgetActions(
 *   () => <Button loading={submitting} onClick={handleOpen}>Speichern</Button>,
 *   [submitting, handleOpen],
 * );
 */
export function useWidgetActions(
    getActions: () => ReactNode,
    deps: DependencyList = [],
): void {
    const {setActions} = useContext(WidgetActionsContext);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        setActions(getActions());
        return () => setActions(null);
    }, [setActions, ...deps]);
}